import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Box, Button, IconButton, Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { dateString } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { renameMac } from 'features/tenant/api/macs';
import { useRequest } from 'shared/hooks';
import { searchTenantRadius } from 'shared/api/tenant-radius';
import { TextEditor } from 'shared/components/data-display/editors';
import ListGroupItem from 'shared/components/styled/listGroupItem';
import { getTenantPath } from 'shared/routes/tenant';
import DeviceDeleteButton from './DeviceDeleteButton';
import { formatTenantName } from '../../shared/utils/formatters';

/**
 * @param {Object} props
 * @param {boolean} props.active - Whether the modal is currently active
 * @param {Function} props.changeActiveDevice - Function to change to a different device
 * @param {Object} props.device - Device object to display
 * @param {Function} props.forceUpdate - Function to force component update
 * @param {boolean} props.hasMore - Whether there are more devices available
 * @param {boolean} props.editEnabled - Whether editing is enabled
 * @param {boolean} props.radiusDataAccessPermission - Whether user has permission to
 *   access radius data
 * @param {Function} props.setActiveDevice - Function to set the active device
 * @param {Object} props.tenant - Tenant object associated with the device
 */
export default function DeviceModal(props) {
  const {
    request,
    isLoading,
  } = useRequest();
  const isMobile = useMediaQuery('(max-width:420px)');

  const {
    active,
    changeActiveDevice,
    device,
    forceUpdate,
    hasMore,
    editEnabled,
    radiusDataAccessPermission,
    setActiveDevice,
    tenant,
  } = props;

  const [updatedName, setUpdatedName] = useState('');
  const [radiusRecords, setRadiusRecords] = useState(undefined);

  /*
   ReadyState attempts to force a wait on loading the dialog until all data has been retrieved
   The alternative is a glitchy partial primary load, with a delayed secondary load.
   */
  const [readyState, setReadyState] = useState(false);

  useEffect(() => {
    async function getRadiusRecords() {
      if (radiusDataAccessPermission) {
        const result = await request(searchTenantRadius(`?username=${device.mac}`));
        if (result && result?.rows.length > 0) {
          setRadiusRecords(result.rows);
        }
      }
    }

    setUpdatedName(device.name);
    if (active && radiusRecords === undefined) {
      setReadyState(false);
      getRadiusRecords()
        .then(setReadyState(true));
    }
  }, [request, active, radiusRecords, device, radiusDataAccessPermission]);

  const onNameUpdate = async () => {
    const result = await request(renameMac(device.id, updatedName));
    if (!result) return null;
    forceUpdate();
    return result;
  };

  const deviceDescriptions = [
    {
      label: 'Tenant',
      skipWhen: () => !tenant.id,
      formatter: () => <Link to={getTenantPath(tenant.id)}>{formatTenantName(tenant)}</Link>,
    },
    {
      label: 'Name',
      key: 'name',
      editInline: editEnabled,
      editor: TextEditor,
    },
    {
      label: 'Created',
      formatter: (d) => dateString(d.created_at),
    },
  ];

  return (
    <>
      {active && readyState && (
        <Dialog open={active} onClose={() => setActiveDevice(0)} fullScreen={isMobile}>
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <IconButton
                disabled={isLoading || !hasMore}
                onClick={() => changeActiveDevice(-1)}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <Typography sx={{
                fontSize: '22px',
              }}
              >
                {device.mac}
              </Typography>
              <IconButton
                disabled={isLoading || !hasMore}
                onClick={() => changeActiveDevice(1)}
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ background: 'cardBackground' }}>
            <DataFormatter
              data={device}
              descriptions={[{
                label: 'Mac',
                key: 'mac',
              }]}
              copyable
            />
            <DataFormatter
              data={device}
              descriptions={deviceDescriptions}
              saveChanges={onNameUpdate}
              updateValue={(_, value) => setUpdatedName(value)}
              updatedData={{ name: updatedName }}
            />
            {radiusRecords && (
              <>
                <div className="flex-between">
                  <h5>Radius</h5>
                </div>
                {radiusRecords && radiusRecords.map((record) => (
                  <ListGroupItem key={`${record.table}:${record.id}`}>
                    <code>{`${record.username} ${record.attribute} ${record.op} ${record.value}`}</code>
                  </ListGroupItem>
                ))}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{
            justifyContent: 'end',
            marginX: 2,
          }}
          >
            {updatedName !== device.name ? (
              <>
                <Button
                  variant={VARIANTS.outlined}
                  onClick={() => setUpdatedName(device.name)}
                >
                  Clear
                </Button>
                <Button variant={VARIANTS.contained} onClick={onNameUpdate}>Save</Button>
              </>
            ) : (
              <>
                <Button
                  variant={VARIANTS.outlined}
                  color={BUTTON_COLORS.primary}
                  onClick={() => setActiveDevice(0)}
                >
                  Close
                </Button>
                {editEnabled && (
                  <DeviceDeleteButton
                    afterDelete={() => {
                      setActiveDevice(0);
                      forceUpdate();
                    }}
                    devices={[device]}
                    editEnabled={editEnabled}
                    tenant={tenant}
                  />
                )}
              </>
            )}
          </DialogActions>
        </Dialog>
      )}
      <div className="table-button-row">
        <Button
          variant={VARIANTS.outlined}
          onClick={() => setActiveDevice(device.id)}
        >
          {device.mac}
        </Button>
      </div>
    </>
  );
}
